import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  AlertCircle,
  AlertTriangle, 
  ArrowDownToLine, 
  BadgeCheck, 
  CalendarClock, 
  CircleHelp, 
  Clock3, 
  Flame,
  LockKeyhole, 
  RefreshCw, 
  ShieldCheck,
  SlidersHorizontal, 
  Sparkles,
  Wallet
} from 'lucide-react';
import type { AllocationPlan, MarketSnapshot, RiskSettings } from '@allocation/risk-core';
import { COPY } from './config/copy';
import { loadMarketSnapshot, formatCurrency, formatPercent } from './lib/market';
import { FanChart } from './components/FanChart';
import { RiskChart } from './components/RiskChart';
import { CapitalStack } from './components/CapitalStack';
import { ActionSummary } from './components/ActionSummary';
import './styles.css';

type ExtendedMarketSnapshot = MarketSnapshot & {
  marketSession?: 'regular' | 'closed';
  sessionMessage?: string;
};

type WorkerMessage = {
  type: 'PLAN_READY' | 'PLAN_ERROR';
  requestId: string;
  payload?: AllocationPlan;
  error?: string;
};

const defaultSettings: RiskSettings = {
  capital: 10_000,
  safetyFloor: 0.7,
  maxPositions: 4,
  maxLossAmount: 700,
  fractionalStep: 0.01,
  staleAfterMinutes: 20,
  cashBenchmarkAnnualRate: 0.04
};

function stateLabel(state?: string, session?: string): string {
  const day = new Date().getDay();
  const isWeekend = day === 0 || day === 6;
  if (session === 'closed' || isWeekend) return 'Market Closed';
  if (state === 'NORMAL') return 'Normal';
  if (state === 'CAUTION') return 'Caution';
  if (state === 'RISK_OFF') return 'Risk Off';
  if (state === 'DATA_STALE') return 'Awaiting Data';
  return 'Evaluating';
}

function stateClass(state?: string, session?: string): string {
  const day = new Date().getDay();
  const isWeekend = day === 0 || day === 6;
  if (session === 'closed' || isWeekend) return 'status-chip status-neutral';
  if (state === 'NORMAL') return 'status-chip status-good';
  if (state === 'CAUTION') return 'status-chip status-caution';
  if (state === 'RISK_OFF') return 'status-chip status-risk';
  return 'status-chip status-neutral';
}

export default function App() {
  const workerRef = useRef<Worker | null>(null);
  const [snapshot, setSnapshot] = useState<ExtendedMarketSnapshot | null>(null);
  const [plan, setPlan] = useState<AllocationPlan | null>(null);
  const [settings, setSettings] = useState<RiskSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const coreSeries = useMemo(() => snapshot?.history.VOO ?? snapshot?.history.SPY ?? [], [snapshot]);
  const topInstructions = useMemo(() => plan?.instructions.filter((item) => item.action !== 'HOLD') ?? [], [plan]);

  const loadSnapshot = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadMarketSnapshot();
      setSnapshot(data as ExtendedMarketSnapshot);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to load market snapshot.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    workerRef.current = new Worker(new URL('./workers/plan.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current.onmessage = (event: MessageEvent<WorkerMessage>) => {
      if (event.data.type === 'PLAN_READY' && event.data.payload) setPlan(event.data.payload);
      if (event.data.type === 'PLAN_ERROR') setError(event.data.error ?? 'Risk calculation failed.');
      setCalculating(false);
    };
    void loadSnapshot();
    return () => workerRef.current?.terminate();
  }, []);

  useEffect(() => {
    if (!snapshot || !workerRef.current) return;
    setCalculating(true);
    workerRef.current.postMessage({ type: 'PLAN', requestId: crypto.randomUUID(), snapshot, settings, holdings: [] });
  }, [snapshot, settings]);

  const updateSetting = <K extends keyof RiskSettings>(key: K, value: RiskSettings[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const dayOfWeek = new Date().getDay();
  const isWeekendNow = dayOfWeek === 0 || dayOfWeek === 6;

  return (
    <main className="app-shell">
      <header className="app-header">
        <div className="site-width header-content">
          <div className="brand-wrap">
            <div className="brand-mark"><LockKeyhole size={18} /></div>
            <div>
              <p className="brand-overline">PRIVATE ALLOCATION RESEARCH</p>
              <h1>{COPY.productName}</h1>
            </div>
          </div>
          <div className="header-actions">
            <span className={stateClass(plan?.state, snapshot?.marketSession)}>
              <i />{stateLabel(plan?.state, snapshot?.marketSession)}
            </span>
            <button className="refresh-button" onClick={() => void loadSnapshot()} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh Snapshot
            </button>
          </div>
        </div>
      </header>

      <section className="hero-section">
        <div className="site-width">
          <div className="hero-grid">
            <div>
              <p className="eyebrow"><Sparkles size={14} />Allocation Research for Capital Preservation</p>
              <h2>Establish the safety floor,<br /><em>then determine each step.</em></h2>
              <p className="hero-copy">{COPY.subtitle}. All outcomes reflect timestamped data, risk bounds, and transparent execution rules.</p>
            </div>
            <div className="hero-proof">
              <div className="proof-icon"><BadgeCheck size={22} /></div>
              <div>
                <strong>No Automated Execution</strong>
                <p>Decision support workspace. Every step requires manual confirmation.</p>
              </div>
            </div>
          </div>
          {error && <div className="notice notice-error"><AlertTriangle size={18} />{error}</div>}
          {snapshot && (
            <div className={snapshot.isStale ? 'data-strip data-stale' : 'data-strip'}>
              <span className="data-live-dot" />
              {isWeekendNow
                ? 'Market is closed for the weekend. Showing the latest available closing snapshot.'
                : (snapshot.sessionMessage || (snapshot.isStale ? COPY.dataStale : COPY.dataFresh))}
              <span className="data-separator" /> Data as of {new Date(snapshot.asOf).toLocaleString('en-US')}
              <span className="data-separator" /> Snapshot {snapshot.cacheVersion}
            </div>
          )}
        </div>
      </section>

      <section className="site-width page-content">
        <div className="summary-grid">
          <SummaryMetric eyebrow="Allocatable Capital" value={formatCurrency(settings.capital)} detail="User defined; excluding taxes and transaction fees" />
          <SummaryMetric eyebrow="Safety Floor Target" value={plan ? formatCurrency(plan.defensiveAmount) : '—'} detail={plan ? `${formatPercent(plan.defensiveAmount / settings.capital, 0)} allocated to defensive capital` : 'Loading risk constraints'} tone="green" />
          <SummaryMetric eyebrow="Estimated Tail Risk" value={plan ? formatCurrency(plan.risk.worstFivePercentAverage * settings.capital) : '—'} detail="Average single-day loss across the worst 5% historical sample" tone="gold" />
          <SummaryMetric
            eyebrow="Current Status"
            value={calculating ? 'Updating' : (isWeekendNow || snapshot?.marketSession === 'closed' ? 'Market Closed' : stateLabel(plan?.state))}
            detail={isWeekendNow ? 'Market is closed for the weekend. Model ready.' : (snapshot?.sessionMessage || plan?.risk.message || 'Reading market data')}
            tone={plan?.state === 'RISK_OFF' ? 'red' : undefined}
          />
        </div>

        <ActionSummary plan={plan} />

        {plan && <CalculationPipeline settings={settings} plan={plan} />}

        {plan && <CapitalStack capital={settings.capital} defensiveAmount={plan.defensiveAmount} riskAmount={plan.riskAmount} />}

        <div className="dashboard-grid">
          <section className="content-column">
            <div className="card card-elevated chart-card">
              <div className="card-heading">
                <div>
                  <p className="section-kicker">Stress Testing</p>
                  <h2 className="section-title">20-Day Forward Volatility Range</h2>
                  <p className="section-copy">Range derived from mean and volatility estimates of historical daily returns. Illustrates uncertainty; does not forecast price direction.</p>
                </div>
                <span className="chart-tag">90% Historical Interval</span>
              </div>
              <FanChart series={coreSeries} />
            </div>

            <div className="card card-elevated chart-card">
              <div className="card-heading">
                <div>
                  <p className="section-kicker">Historical Stress</p>
                  <h2 className="section-title">Drawdown from Historical Peaks</h2>
                  <p className="section-copy">Illustrates past drawdown magnitude across the sample period. Does not predict the timing of future drawdowns.</p>
                </div>
                <ArrowDownToLine size={22} className="heading-icon" />
              </div>
              <RiskChart series={coreSeries} />
            </div>
          </section>

          <aside className="sidebar-column">
            <section className="card plan-card">
              <div className="card-heading compact">
                <div><p className="section-kicker">Execution Grid</p><h2 className="section-title">Tranche Schedule</h2></div>
                <CalendarClock size={21} className="heading-icon" />
              </div>
              <p className="section-copy">Displays items requiring attention. Review execution conditions before taking action.</p>
              <div className="instruction-list">
                {topInstructions.map((item) => (
                  <article className="instruction-item" key={item.ticker}>
                    <div className="instruction-top">
                      <div>
                        <strong>{item.ticker}</strong>
                        <span className={item.action === 'BUY' ? 'action-badge action-buy' : 'action-badge action-sell'}>
                          {item.action === 'BUY' ? 'Tranche Allocation' : 'Restore Floor'}
                        </span>
                      </div>
                      <b>{formatCurrency(item.amount)}</b>
                    </div>
                    <p>{item.shares.toFixed(2)} shares · {item.rationale}</p>
                    {item.tranches.length > 0 && (
                      <div className="tranche-row">
                        {item.tranches.map((slice) => (
                          <span key={slice.tranche}>Tranche {slice.tranche} <b>{slice.shares.toFixed(2)} shares</b></span>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
                {topInstructions.length === 0 && (
                  <div className="empty-plan">
                    <Clock3 size={19} />
                    <p>No execution actions required. Safety floor and parameters remain unchanged.</p>
                  </div>
                )}
              </div>
            </section>

            <section className="card assumptions-card">
              <div className="card-heading compact">
                <div><p className="section-kicker">Parametric Audit</p><h2 className="section-title">Rules & Substitutions</h2></div>
                <CircleHelp size={20} className="heading-icon" />
              </div>
              <ul style={{ paddingLeft: '1.2rem', fontSize: '0.85rem', lineHeight: '1.6', color: '#475569' }}>
                <li>
                  <strong>Capital Floor Constraint:</strong> Target <code>{formatCurrency(settings.capital)} × {formatPercent(settings.safetyFloor, 0)} = {plan ? formatCurrency(plan.defensiveAmount) : '...'}</code> is strictly locked in cash/ultra-short bonds.
                </li>
                <li>
                  <strong>Tail-Risk Tolerance:</strong> Worst 5% daily expected loss <code>{plan ? formatCurrency(plan.risk.worstFivePercentAverage * settings.capital) : '...'}</code> is bounded by max loss cap <code>{formatCurrency(settings.maxLossAmount)}</code>.
                </li>
                <li>
                  <strong>Position Cap:</strong> Active risk pool <code>{plan ? formatCurrency(plan.riskAmount) : '...'}</code> is subdivided across a maximum of <code>{settings.maxPositions}</code> distinct assets.
                </li>
                <li>
                  <strong>Execution Granularity:</strong> Fractional trade sizing is constrained to step size <code>{settings.fractionalStep}</code> shares.
                </li>
              </ul>
            </section>
          </aside>
        </div>

        <section className="settings-section">
          <div className="settings-intro">
            <div className="settings-icon"><SlidersHorizontal size={20} /></div>
            <div>
              <p className="section-kicker">Risk Boundaries</p>
              <h2 className="section-title">Adjust Constraints & Recalculate</h2>
              <p className="section-copy">Adjustments respect asset ceilings and minimum safety floors; an infeasible status indicates constraints were exceeded.</p>
            </div>
          </div>
          <div className="settings-controls">
            <Control label="Allocatable Capital" value={formatCurrency(settings.capital)} help="Amount designated for this allocation model." />
            <input
              className="number-field"
              type="number"
              min="1000"
              step="500"
              value={settings.capital}
              aria-label="Allocatable Capital"
              onChange={(event) => updateSetting('capital', Math.max(0, Number(event.target.value)))}
            />
            <RangeControl label="Defensive Bucket Floor" value={formatPercent(settings.safetyFloor, 0)} min={0.6} max={0.9} step={0.05} current={settings.safetyFloor} onChange={(value) => updateSetting('safetyFloor', value)} help="Default 70%; higher allocations reduce market exposure." />
            <RangeControl label="Max Acceptable Tail Loss" value={formatCurrency(settings.maxLossAmount)} min={100} max={Math.max(1000, settings.capital * 0.2)} step={50} current={settings.maxLossAmount} onChange={(value) => updateSetting('maxLossAmount', value)} help="Limits average estimated loss within the worst 5% historical sample." />
            <RangeControl label="Max Positions" value={`${settings.maxPositions}`} min={2} max={5} step={1} current={settings.maxPositions} onChange={(value) => updateSetting('maxPositions', value)} help="Fewer positions simplify tracking and execution management." />
          </div>
        </section>
      </section>

      <footer className="app-footer">
        <div className="site-width">
          <p>{COPY.researchOnly}</p>
          <p>Outputs depend on dataset versioning and model assumptions. Independently verify pricing, liquidity, fees, taxes, and risk parameters before placing trades.</p>
        </div>
      </footer>
    </main>
  );
}

function CalculationPipeline({ settings, plan }: { settings: RiskSettings; plan: AllocationPlan }) {
  const tailRiskAmount = plan.risk.worstFivePercentAverage * settings.capital;
  const isCvarExceeded = tailRiskAmount > settings.maxLossAmount;

  return (
    <section className="card card-elevated pipeline-section" style={{ margin: '1.5rem 0' }}>
      <div className="card-heading compact">
        <div>
          <p className="section-kicker">Transparent Methodology</p>
          <h2 className="section-title">Deterministic Allocation Pipeline</h2>
        </div>
      </div>
      <p className="section-copy" style={{ marginBottom: '1.25rem' }}>
        Step-by-step derivation converting raw capital and market variance constraints into executable allocations.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', alignItems: 'stretch' }}>
        <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#64748b' }}>
            <Wallet size={16} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>01. Capital Designated</span>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{formatCurrency(settings.capital)}</div>
          <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            User baseline designated for model distribution.
          </p>
        </div>

        <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#166534' }}>
            <ShieldCheck size={16} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>02. Safety Floor Locked</span>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#15803d' }}>{formatCurrency(plan.defensiveAmount)}</div>
          <p style={{ fontSize: '0.75rem', color: '#166534', marginTop: '0.25rem' }}>
            {formatCurrency(settings.capital)} × {formatPercent(settings.safetyFloor, 0)} allocated to low-beta defensive buffer.
          </p>
        </div>

        <div style={{ background: '#fffbeb', padding: '1rem', borderRadius: '8px', border: '1px solid #fef3c7' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#92400e' }}>
            <Flame size={16} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>03. Active Risk Pool</span>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#b45309' }}>{formatCurrency(plan.riskAmount)}</div>
          <p style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.25rem' }}>
            Remaining {formatPercent(1 - settings.safetyFloor, 0)} distributed across max {settings.maxPositions} growth tranches.
          </p>
        </div>

        <div style={{ background: isCvarExceeded ? '#fef2f2' : '#f8fafc', padding: '1rem', borderRadius: '8px', border: `1px solid ${isCvarExceeded ? '#fecaca' : '#e2e8f0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: isCvarExceeded ? '#991b1b' : '#475569' }}>
            <AlertCircle size={16} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>04. Tail Stress Test</span>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: isCvarExceeded ? '#dc2626' : '#334155' }}>
            {formatCurrency(tailRiskAmount)}
          </div>
          <p style={{ fontSize: '0.75rem', color: isCvarExceeded ? '#991b1b' : '#64748b', marginTop: '0.25rem' }}>
            5% worst CVaR vs. limit {formatCurrency(settings.maxLossAmount)} ({isCvarExceeded ? 'Constraint exceeded' : 'Within tolerance'}).
          </p>
        </div>
      </div>
    </section>
  );
}

function SummaryMetric({ eyebrow, value, detail, tone }: { eyebrow: string; value: string; detail: string; tone?: 'green' | 'gold' | 'red' }) {
  return (
    <article className={`summary-metric ${tone ? `metric-${tone}` : ''}`}>
      <p>{eyebrow}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </article>
  );
}

function Control({ label, value, help }: { label: string; value: string; help: string }) {
  return (
    <div className="control-label">
      <div>
        <p>{label}</p>
        <span>{help}</span>
      </div>
      <strong>{value}</strong>
    </div>
  );
}

function RangeControl({ label, value, help, min, max, step, current, onChange }: { label: string; value: string; help: string; min: number; max: number; step: number; current: number; onChange: (value: number) => void }) {
  return (
    <div className="range-control">
      <Control label={label} value={value} help={help} />
      <input className="refined-range" type="range" min={min} max={max} step={step} value={current} onChange={(event) => onChange(Number(event.target.value))} />
    </div>
  );
}